import { ChevronLeft, Copy, History } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { eventEntityLabel, eventSentence, type EventLogRow } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { formatDateTime } from '../../../../lib/format';
import { useToasts } from '../../../../stores/toasts';
import { clipped, factsOf, fieldTableOf, type FieldTable } from './event-log-words';
import { OpenRecordLink } from './OpenRecordLink';
import styles from './EventLogDetail.module.css';

// Event log A's pane: the clicked event — its kind, the sentence, the exact time and the technical name; "What
// changed" as a field table with the changed rows marked; "About this event"; then "Everything that happened to this
// record", Open, Copy id and "Show raw data" (opened in place). Values hidden from the viewer show as — (#5).

interface EventLogDetailProps {
  readonly row: EventLogRow;
  /** The drill-down. Absent inside the story itself. */
  readonly onStory?: (row: EventLogRow) => void;
  /** On a phone: back to the list. */
  readonly onBack?: () => void;
  readonly backLabel?: string;
}

export function EventLogDetail({ row, onStory, onBack, backLabel = 'Event log' }: EventLogDetailProps): ReactNode {
  const [isRawOpen, setRawOpen] = useState(false);
  const [rawFor, setRawFor] = useState(row.id);
  // Another event opened: its raw data starts closed.
  if (rawFor !== row.id) {
    setRawFor(row.id);
    setRawOpen(false);
  }
  const showToast = useToasts((state) => state.show);
  const table = fieldTableOf(row);
  const facts = factsOf(row);

  const copyId = (): void => {
    navigator.clipboard
      .writeText(row.entityId)
      .then(() => showToast({ key: 'event-log-copy', tone: 'success', message: 'Record id copied', detail: row.entityId }))
      .catch(() => showToast({ key: 'event-log-copy', tone: 'danger', message: "The id wasn't copied", detail: 'Select it under Record id and copy it from there.' }));
  };

  return (
    <article className={styles.detail} aria-labelledby="event-log-detail-title">
      {onBack && (
        <div className={styles.back}>
          <Button variant="ghost" size="sm" icon={ChevronLeft} onClick={onBack}>
            {backLabel}
          </Button>
        </div>
      )}

      <header className={styles.head}>
        <span className={styles.kind}>{eventEntityLabel(row.entityType)}</span>
        <h2 id="event-log-detail-title" className={styles.title}>
          {eventSentence(row)}
        </h2>
        <p className={styles.when}>
          {formatDateTime(row.occurredAt)} · <code className={styles.code}>{row.eventType}</code>
        </p>
      </header>

      <section className={styles.section} aria-labelledby="event-log-changed">
        <h3 id="event-log-changed" className={styles.heading}>
          {table.kind === 'diff' ? 'What changed' : 'What was recorded'}
        </h3>
        <ChangeTable table={table} />
        {row.restricted.length > 0 && <p className={styles.note}>Some values are hidden from your role and show as —.</p>}
        {row.record?.isAnonymised && <p className={styles.note}>This person was anonymised, so whatever identified them shows as —.</p>}
      </section>

      <section className={styles.section} aria-labelledby="event-log-about">
        <h3 id="event-log-about" className={styles.heading}>
          About this event
        </h3>
        <dl className={styles.facts}>
          {facts.map((fact) => (
            <div key={fact.label} className={styles.fact}>
              <dt>{fact.label}</dt>
              <dd className={fact.isCode ? styles.mono : undefined}>{fact.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <div className={styles.actions}>
        {onStory && (
          <Button variant="solid" size="sm" icon={History} onClick={() => onStory(row)}>
            Everything that happened to this record
          </Button>
        )}
        <OpenRecordLink entityType={row.entityType} entityId={row.entityId} className={styles.link} />
        <Button variant="quiet" size="sm" icon={Copy} onClick={copyId}>
          Copy id
        </Button>
        <Button variant="quiet" size="sm" aria-expanded={isRawOpen} onClick={() => setRawOpen((open) => !open)}>
          {isRawOpen ? 'Hide raw data' : 'Show raw data'}
        </Button>
      </div>

      {isRawOpen && (
        <section className={styles.raw} aria-label="Raw data">
          <RawBlock label="Before" value={row.payloadBefore} />
          <RawBlock label="After" value={row.payloadAfter} />
          <RawBlock label="Metadata" value={row.metadata} />
        </section>
      )}
    </article>
  );
}

function ChangeTable({ table }: { readonly table: FieldTable }): ReactNode {
  if (table.kind === 'none' || table.rows.length === 0) {
    return <p className={styles.note}>Nothing was recorded about what changed: this event says that something happened.</p>;
  }
  const isDiff = table.kind === 'diff';
  return (
    <table className={styles.diff}>
      <thead>
        <tr>
          <th scope="col">Field</th>
          {isDiff ? (
            <>
              <th scope="col">Before</th>
              <th scope="col">After</th>
            </>
          ) : (
            <th scope="col">Value</th>
          )}
        </tr>
      </thead>
      <tbody>
        {table.rows.map((row) => (
          <tr key={row.key} data-changed={row.isChanged || undefined}>
            <th scope="row">{row.label}</th>
            {isDiff && (
              <td className={styles.was} title={row.before} data-hidden={row.isBeforeHidden || undefined}>
                {clipped(row.before)}
              </td>
            )}
            {table.kind === 'before' ? (
              <td title={row.before} data-hidden={row.isBeforeHidden || undefined}>
                {clipped(row.before)}
              </td>
            ) : (
              <td title={row.after} data-hidden={row.isAfterHidden || undefined}>
                {row.isChanged ? <b>{clipped(row.after)}</b> : clipped(row.after)}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RawBlock({ label, value }: { readonly label: string; readonly value: unknown }): ReactNode {
  return (
    <div className={styles.rawBlock}>
      <h4 className={styles.rawLabel}>{label}</h4>
      <pre className={styles.json}>{value === null || value === undefined ? '—' : JSON.stringify(value, null, 2)}</pre>
    </div>
  );
}
